import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";



import DataTable from "components/table/DataTable";



import {

    getExpenses,

    deleteExpense,

    submitExpense,

    approveExpense,

    rejectExpense,

    markExpensePaid,

} from "services/api/expenseApi";



import {

    showSuccess,

    showError,

} from "utils/toast";



import { expenseColumns } from "./columns";

import ExpenseModal from "./ExpenseModal";



// ============================================================

// INITIAL FILTERS

// ============================================================



const INITIAL_FILTERS = {

    employeeId: "",

    fromLocation: "",

    toLocation: "",

    status: "",

    fromDate: "",

    toDate: "",

};



// ============================================================

// FORMATTERS

// ============================================================



const formatDate = (value) => {

    if (!value) return "-";



    const date = new Date(value);



    if (Number.isNaN(date.getTime())) return "-";



    return date.toLocaleDateString("en-IN", {

        day: "2-digit",

        month: "short",

        year: "numeric",

    });

};



const formatCurrency = (value) => {

    if (value === null || value === undefined || value === "") {

        return "-";

    }



    const amount = Number(value);



    if (Number.isNaN(amount)) {

        return `₹${value}`;

    }



    return `₹${amount.toLocaleString("en-IN", {

        minimumFractionDigits: 2,

        maximumFractionDigits: 2,

    })}`;

};



const labelize = (value) => {

    if (!value) return "-";



    return String(value)

        .replace(/_/g, " ")

        .replace(/\b\w/g, (char) => char.toUpperCase());

};



// ============================================================

// GROUP KEY

// IMPORTANT:

// Same employee + same date + same VISIT = one UI row.

// Database records remain separate.

// Expenses without a visit remain separate.

// ============================================================



const getDateKey = (value) => {

    if (!value) return "";



    const raw = String(value);



    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {

        return raw.slice(0, 10);

    }



    const date = new Date(value);



    if (Number.isNaN(date.getTime())) return "";



    const year = date.getFullYear();

    const month = String(date.getMonth() + 1).padStart(2, "0");

    const day = String(date.getDate()).padStart(2, "0");



    return `${year}-${month}-${day}`;

};



const getEmployeeKey = (expense) =>

    String(

        expense?.employeeId ??

            expense?.employee?.id ??

            expense?.employee?.employeeId ??

            "no-employee"

    );



const getVisitKey = (expense) =>

    expense?.visitId ??

    expense?.visit?.id ??

    null;



const getExpenseGroupKey = (expense) => {

    const visitId = getVisitKey(expense);



    // Do not merge unrelated expenses when there is no visit.

    if (!visitId) {

        return `single:${expense?.id}`;

    }



    return [

        "visit",

        getEmployeeKey(expense),

        getDateKey(expense?.expenseDate),

        String(visitId),

    ].join(":");

};



const sumAmount = (items, field) =>

    items.reduce((total, item) => {

        const value = Number(item?.[field]);



        return Number.isFinite(value) ? total + value : total;

    }, 0);



const uniqueValues = (items, getter) =>

    Array.from(

        new Set(

            items

                .map(getter)

                .filter(

                    (value) =>

                        value !== null &&

                        value !== undefined &&

                        String(value).trim() !== ""

                )

                .map((value) => String(value))

        )

    );



const getGroupStatus = (items) => {

    const statuses = uniqueValues(items, (item) => item?.status);



    if (!statuses.length) return "";



    if (statuses.length === 1) return statuses[0];



    // A group can legitimately contain lines in different states.

    // Keep that visible instead of pretending it has one status.

    return "mixed";

};



const getGroupTitle = (items) => {

    const types = uniqueValues(items, (item) => item?.expenseType);



    if (!types.length) return "Expense";



    return types.map(labelize).join(" + ");

};



const groupExpensesForDisplay = (items) => {

    const groups = new Map();



    items.forEach((expense) => {

        const key = getExpenseGroupKey(expense);



        if (!groups.has(key)) {

            groups.set(key, []);

        }



        groups.get(key).push(expense);

    });



    return Array.from(groups.entries()).map(([groupKey, groupItems]) => {

        const first = groupItems[0];



        const approvedValues = groupItems

            .map((item) => item?.approvedAmount)

            .filter(

                (value) =>

                    value !== null &&

                    value !== undefined &&

                    value !== ""

            );



        const fromLocations = uniqueValues(

            groupItems,

            (item) => item?.fromLocation

        );



        const toLocations = uniqueValues(

            groupItems,

            (item) => item?.toLocation

        );



        return {

            ...first,



            id: first?.id,



            groupId: groupKey,



            isExpenseGroup: groupItems.length > 1,



            expenseCount: groupItems.length,



            expenses: groupItems,



            expenseTypes: uniqueValues(

                groupItems,

                (item) => item?.expenseType

            ),



            groupTitle: getGroupTitle(groupItems),



            claimedAmount: sumAmount(

                groupItems,

                "claimedAmount"

            ),



            approvedAmount:

                approvedValues.length > 0

                    ? sumAmount(

                          groupItems,

                          "approvedAmount"

                      )

                    : null,



            status: getGroupStatus(groupItems),



            fromLocation:

                fromLocations.length > 0

                    ? fromLocations.join(" / ")

                    : first?.fromLocation || "",



            toLocation:

                toLocations.length > 0

                    ? toLocations.join(" / ")

                    : first?.toLocation || "",

        };

    });

};



// ============================================================

// MAIN

// ============================================================



export default function Expenses() {

    const [expenses, setExpenses] = useState([]);

    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(1);



    const [openModal, setOpenModal] =

        useState(false);



    const [selectedItem, setSelectedItem] =

        useState(null);



    /*

     * Single ExpenseModal is used for:

     * - Create

     * - Edit

     * - View

     *

     * This keeps the existing flow simple and avoids

     * maintaining two different expense detail UIs.

     */

    const [modalMode, setModalMode] =

        useState("create");



    const [filters, setFilters] =

        useState(INITIAL_FILTERS);



    const [appliedFilters, setAppliedFilters] =

        useState(INITIAL_FILTERS);



    // ========================================================

    // LOAD DATA

    // ========================================================





const loadData = async () => {

    console.log("[Expenses] loadData() called", {

        page,

        appliedFilters,

    });



    try {

        setLoading(true);



        console.log("[Expenses] Calling GET /expenses");



        const response = await getExpenses(page, 10, {

            employeeId: appliedFilters.employeeId || undefined,

            fromLocation: appliedFilters.fromLocation.trim() || undefined,

            toLocation: appliedFilters.toLocation.trim() || undefined,

            status: appliedFilters.status || undefined,

            fromDate: appliedFilters.fromDate || undefined,

            toDate: appliedFilters.toDate || undefined,

        });



        console.log("[Expenses] API response:", response);



        // Support common response structures.

        const rawExpenses =

            response?.data?.expenses ??

            response?.expenses ??

            response?.data?.data?.expenses ??

            [];



        console.log("[Expenses] Records received:", rawExpenses.length);



        setExpenses(groupExpensesForDisplay(rawExpenses));

    } catch (error) {

        console.error("[Expenses] API failed:", {

            message: error?.message,

            status: error?.response?.status,

            data: error?.response?.data,

        });



        showError(

            error?.response?.data?.message ||

            "Unable to load expenses."

        );

    } finally {

        setLoading(false);

    }

};



useEffect(() => {

    console.log("[Expenses] Component mounted / filters changed");

    loadData();

}, [page, appliedFilters]);





    useEffect(() => {

        loadData();

    }, [page, appliedFilters]);



    // ========================================================

    // EMPLOYEE OPTIONS

    // ========================================================



    const employeeOptions = useMemo(() => {

        const map = new Map();



        expenses.forEach((expense) => {

            const sourceItems =

                Array.isArray(expense?.expenses)

                    ? expense.expenses

                    : [expense];



            sourceItems.forEach((item) => {

                const employee =

                    item?.employee;



                if (!employee) return;



                const id =

                    employee.id ||

                    employee.employeeId;



                const name =

                    employee.name ||

                    employee.fullName ||

                    employee.employeeName;



                if (id && name) {

                    map.set(

                        String(id),

                        {

                            id: String(id),

                            name,

                            code:

                                employee.employeeCode ||

                                employee.code ||

                                "",

                        }

                    );

                }

            });

        });



        return Array.from(

            map.values()

        ).sort((a, b) =>

            a.name.localeCompare(b.name)

        );

    }, [expenses]);



    // ========================================================

    // FILTERS

    // ========================================================



    const updateFilter = (

        key,

        value

    ) => {

        setFilters((prev) => ({

            ...prev,

            [key]: value,

        }));

    };



    const handleApplyFilters = () => {

        setPage(1);

        setAppliedFilters({

            ...filters,

        });

    };



    const handleClearFilters = () => {

        const cleared = {

            ...INITIAL_FILTERS,

        };



        setFilters(cleared);

        setAppliedFilters(cleared);

        setPage(1);

    };



    // ========================================================

    // ADD / EDIT

    // ========================================================



    const handleAdd = () => {

        setSelectedItem(null);

        setModalMode("create");

        setOpenModal(true);

    };



    const handleEdit = (expense) => {

        setSelectedItem(expense);

        setModalMode("edit");

        setOpenModal(true);

    };



    const handleView = (expense) => {

        setSelectedItem(expense);

        setModalMode("view");

        setOpenModal(true);

    };



    const handleCloseModal = () => {

        setOpenModal(false);

        setSelectedItem(null);

        setModalMode("create");

    };



    // ========================================================

    // DELETE / CANCEL

    // ========================================================



    const handleDelete = async (id) => {

        if (

            !window.confirm(

                "Cancel this expense?"

            )

        ) {

            return;

        }



        try {

            await deleteExpense(id);



            showSuccess(

                "Expense cancelled successfully."

            );



            await loadData();

        } catch (error) {

            showError(

                error?.response?.data?.message ||

                    "Unable to cancel expense."

            );

        }

    };



    // ========================================================

// SUBMIT

// ========================================================



const handleSubmit = async (expenseIds) => {

    /*

     * Always work with an array internally.

     *

     * Supports both:

     * - Single expense ID

     * - Group of expense IDs

     */

    const ids = Array.isArray(expenseIds)

        ? expenseIds

        : [expenseIds];



    /*

     * Clean and normalize IDs.

     */

    const validIds = ids

        .map((item) => {

            /*

             * Safety:

             * If an object is accidentally passed,

             * extract its ID.

             */

            if (

                item &&

                typeof item === "object"

            ) {

                return item.id;

            }



            return item;

        })

        .filter(

            (id) =>

                id !== undefined &&

                id !== null &&

                String(id).trim() !== ""

        )

        .map((id) => String(id));



    /*

     * Remove duplicate IDs.

     */

    const uniqueIds = [

        ...new Set(validIds),

    ];



    /*

     * Nothing to submit.

     */

    if (!uniqueIds.length) {

        showError(

            "No valid expenses found to submit."

        );



        return;

    }



    /*

     * Confirmation.

     */

    const confirmMessage =

        uniqueIds.length > 1

            ? `Submit ${uniqueIds.length} expenses for approval?`

            : "Submit this expense for approval?";



    if (!window.confirm(confirmMessage)) {

        return;

    }



    try {

        /*

         * Keep track of successfully submitted

         * and failed expenses.

         */

        const successfulIds = [];

        const failedItems = [];



        /*

         * IMPORTANT:

         *

         * Backend submit API accepts ONE expense ID.

         *

         * Therefore submit each expense separately.

         */

        for (const expenseId of uniqueIds) {

            try {

                console.log(

                    "Submitting expense:",

                    expenseId

                );



                await submitExpense(

                    expenseId

                );



                successfulIds.push(

                    expenseId

                );

            } catch (error) {

                console.error(

                    `Failed to submit expense ${expenseId}:`,

                    error

                );



                failedItems.push({

                    id: expenseId,

                    error,

                });

            }

        }



        /*

         * Refresh the table after all API calls.

         *

         * This is important because the grouped

         * status needs to be recalculated.

         */

        await loadData();



        /*

         * ====================================================

         * SUCCESS

         * ====================================================

         */



        if (

            successfulIds.length > 0 &&

            failedItems.length === 0

        ) {

            if (successfulIds.length === 1) {

                showSuccess(

                    "Expense submitted successfully."

                );

            } else {

                showSuccess(

                    `All ${successfulIds.length} expenses submitted successfully.`

                );

            }



            return;

        }



        /*

         * ====================================================

         * PARTIAL SUCCESS

         * ====================================================

         *

         * Some expenses submitted but some failed.

         */

        if (

            successfulIds.length > 0 &&

            failedItems.length > 0

        ) {

            showError(

                `${successfulIds.length} expense${

                    successfulIds.length > 1

                        ? "s"

                        : ""

                } submitted successfully, but ${

                    failedItems.length

                } failed.`

            );



            return;

        }



        /*

         * ====================================================

         * COMPLETE FAILURE

         * ====================================================

         */



        if (

            successfulIds.length === 0 &&

            failedItems.length > 0

        ) {

            /*

             * Get the first API error message.

             */

            const firstError =

                failedItems[0]?.error;



            const message =

                firstError?.response?.data

                    ?.message ||

                firstError?.message ||

                "Unable to submit expense.";



            showError(message);



            return;

        }



    } catch (error) {

        /*

         * Unexpected error outside individual

         * API calls.

         */

        console.error(

            "Submit expenses error:",

            error

        );



        showError(

            error?.response?.data?.message ||

            error?.message ||

            "Unable to submit expenses."

        );

    }

};



    // ========================================================

    // APPROVE

    // ========================================================



    const handleApprove = async (target) => {

        const isGroup =

            target &&

            Array.isArray(target?.expenses) &&

            target.expenses.length > 1;



        const lines = isGroup

            ? target.expenses.filter(

                  (line) => line?.status === "submitted"

              )

            : [target];



        if (!lines.length) {

            showError("No submitted expense found to approve.");

            return;

        }



        const defaultAmount = isGroup

            ? lines.reduce(

                  (total, line) =>

                      total +

                      (Number(line?.claimedAmount) || 0),

                  0

              )

            : Number(lines[0]?.claimedAmount) || 0;



        const approvedAmount = window.prompt(

            isGroup

                ? `Total approved amount for ${lines.length} expenses`

                : "Approved amount",

            defaultAmount > 0

                ? defaultAmount.toFixed(2)

                : ""

        );



        if (approvedAmount === null) {

            return;

        }



        const totalApproved = Number(approvedAmount);



        if (

            approvedAmount.trim() === "" ||

            !Number.isFinite(totalApproved) ||

            totalApproved < 0

        ) {

            showError("Valid approved amount is required.");

            return;

        }



        try {

            if (!isGroup) {

                await approveExpense(lines[0].id, {

                    approvedAmount: approvedAmount.trim(),

                });

            } else {

                const totalClaimed = lines.reduce(

                    (total, line) =>

                        total +

                        (Number(line?.claimedAmount) || 0),

                    0

                );



                let allocated = 0;



                for (let index = 0; index < lines.length; index += 1) {

                    const line = lines[index];

                    const claimed = Number(line?.claimedAmount) || 0;



                    const amount =

                        index === lines.length - 1

                            ? Math.max(

                                  0,

                                  totalApproved - allocated

                              )

                            : totalClaimed > 0

                            ? Number(

                                  (

                                      (totalApproved * claimed) /

                                      totalClaimed

                                  ).toFixed(2)

                              )

                            : 0;



                    allocated += amount;



                    await approveExpense(line.id, {

                        approvedAmount: amount.toFixed(2),

                    });

                }

            }



            showSuccess(

                isGroup

                    ? `All ${lines.length} expenses approved successfully.`

                    : "Expense approved successfully."

            );



            await loadData();

        } catch (error) {

            showError(

                error?.response?.data?.message ||

                    "Unable to approve expense."

            );

        }

    };



    // ========================================================

    // REJECT

    // ========================================================



    const handleReject = async (target) => {

        const isGroup =

            target &&

            Array.isArray(target?.expenses) &&

            target.expenses.length > 1;



        const lines = isGroup

            ? target.expenses.filter(

                  (line) => line?.status === "submitted"

              )

            : [target];



        if (!lines.length) {

            showError("No submitted expense found to reject.");

            return;

        }



        const reason = window.prompt(

            isGroup

                ? `Rejection reason for ${lines.length} expenses`

                : "Rejection reason"

        );



        if (!reason || !reason.trim()) {

            return;

        }



        try {

            for (const line of lines) {

                await rejectExpense(line.id, reason.trim());

            }



            showSuccess(

                isGroup

                    ? `All ${lines.length} expenses rejected successfully.`

                    : "Expense rejected successfully."

            );



            await loadData();

        } catch (error) {

            showError(

                error?.response?.data?.message ||

                    "Unable to reject expense."

            );

        }

    };



    // ========================================================

    // MARK PAID

    // ========================================================



    const handleMarkPaid = async (target) => {

        const isGroup =

            target &&

            Array.isArray(target?.expenses) &&

            target.expenses.length > 1;



        const lines = isGroup

            ? target.expenses.filter(

                  (line) => line?.status === "approved"

              )

            : [target];



        if (!lines.length) {

            showError("No approved expense found to mark as paid.");

            return;

        }



        const paymentRefNo = window.prompt(

            "Payment reference number (optional)"

        );



        try {

            for (const line of lines) {

                await markExpensePaid(line.id, {

                    paymentRefNo:

                        paymentRefNo?.trim() ||

                        undefined,

                });

            }



            showSuccess(

                isGroup

                    ? `All ${lines.length} expenses marked as paid.`

                    : "Expense marked as paid."

            );



            await loadData();

        } catch (error) {

            showError(

                error?.response?.data?.message ||

                    "Unable to mark expense as paid."

            );

        }

    };



    // ========================================================

    // EXPORT EXCEL
    // ========================================================

    const handleExport = () => {
        try {
            // Export only the rows currently displayed in the Expenses table.
            if (!Array.isArray(expenses) || expenses.length === 0) {
                showError("No expenses available to export.");
                return;
            }

            const getEmployeeName = (item) =>
                item?.employee?.name ||
                item?.employee?.fullName ||
                item?.employee?.employeeName ||
                "-";

            const getEmployeeCode = (item) =>
                item?.employee?.employeeCode ||
                item?.employee?.code ||
                "-";

            const getVisitName = (item) =>
                item?.visit?.school?.name ||
                item?.visit?.party?.name ||
                item?.visit?.purpose ||
                item?.school?.name ||
                item?.party?.name ||
                item?.purpose ||
                "-";

            const formatCell = (value) => {
                if (value === null || value === undefined || value === "") {
                    return "";
                }
                if (value instanceof Date) return value.toLocaleString();
                if (Array.isArray(value)) {
                    return value.map((entry) =>
                        entry && typeof entry === "object"
                            ? (entry.name || entry.title || JSON.stringify(entry))
                            : String(entry ?? "")
                    ).join(", ");
                }
                if (typeof value === "object") {
                    return value.name || value.fullName || value.title || JSON.stringify(value);
                }
                return value;
            };

            // Support common field names used for Team Lead / Admin review remarks.
            // If the backend uses different property names, add them to these lists.
            const firstRemark = (item, keys) => {
                for (const key of keys) {
                    const value = item?.[key];
                    if (value !== null && value !== undefined && String(value).trim() !== "") {
                        return formatCell(value);
                    }
                }
                return "";
            };

            const getTeamLeadRemark = (item) => firstRemark(item, [
                "teamLeadRemark", "teamLeadRemarks", "teamLeaderRemark", "teamLeaderRemarks",
                "tlRemark", "tlRemarks", "team_lead_remark", "team_lead_remarks",
                "team_leader_remark", "team_leader_remarks", "tl_remarks",
            ]);

            const getAdminRemark = (item) => firstRemark(item, [
                "adminRemark", "adminRemarks", "administratorRemark", "administratorRemarks",
                "admin_remarks", "admin_remark", "approvalRemark", "approvalRemarks",
                "approval_remarks", "managerRemark", "managerRemarks", "manager_remarks",
            ]);

            const joinRemarks = (items, getter) => uniqueValues(items, getter).join(" | ");

            // Grouped summary is retained on a separate sheet for reference.
            const groupedSummaryRows = expenses.map((expense) => {
                const sourceItems = Array.isArray(expense?.expenses) && expense.expenses.length
                    ? expense.expenses
                    : [expense];

                const employee = sourceItems.find((item) => item?.employee)?.employee
                    || expense?.employee
                    || {};

                const visitNames = uniqueValues(sourceItems, getVisitName);
                const expenseTypes = Array.isArray(expense?.expenseTypes)
                    ? expense.expenseTypes.map(labelize).join(" + ")
                    : labelize(expense?.expenseType);

                return {
                    "Expense Date": formatDate(expense?.expenseDate),
                    "Employee": employee.name || employee.fullName || employee.employeeName || getEmployeeName(expense),
                    "Employee Code": employee.employeeCode || employee.code || getEmployeeCode(expense),
                    "Visit / School / Party": visitNames.join(" / ") || getVisitName(expense),
                    "From Location": expense?.fromLocation || uniqueValues(sourceItems, (item) => item?.fromLocation).join(" / "),
                    "To Location": expense?.toLocation || uniqueValues(sourceItems, (item) => item?.toLocation).join(" / "),
                    "Expense Types": expenseTypes || "-",
                    "Claimed Amount": Number(expense?.claimedAmount ?? sumAmount(sourceItems, "claimedAmount")) || 0,
                    "Approved Amount": expense?.approvedAmount ?? "",
                    "Status": labelize(expense?.status),
                    "Expense Count": expense?.expenseCount || sourceItems.length,
                    "Visit ID": expense?.visitId ?? sourceItems.map((item) => item?.visitId).filter(Boolean).join(", "),
                    "Salesperson Remarks": joinRemarks(sourceItems, (item) => item?.description || item?.remarks),
                    "Team Lead Remarks": joinRemarks(sourceItems, getTeamLeadRemark),
                    "Admin Remarks": joinRemarks(sourceItems, getAdminRemark),
                };
            });

            // Main Expenses sheet: flatten grouped table rows so each expense type
            // has its own row and its own claimed/approved amount.
            const detailRows = [];
            expenses.forEach((group) => {
                const sourceItems = Array.isArray(group?.expenses) && group.expenses.length
                    ? group.expenses
                    : [group];

                sourceItems.forEach((item, index) => {
                    detailRows.push({
                        "Group / Table Row": group?.groupTitle || group?.groupId || group?.id || "",
                        "Expense ID": item?.id ?? "",
                        "Expense Date": formatDate(item?.expenseDate),
                        "Employee": getEmployeeName(item),
                        "Employee Code": getEmployeeCode(item),
                        "Visit / School / Party": getVisitName(item),
                        "Expense Type": labelize(item?.expenseType),
                        "Title": item?.title ?? "",
                        "Description / Remarks": item?.description ?? item?.remarks ?? "",
                        "Team Lead Remarks": getTeamLeadRemark(item),
                        "Admin Remarks": getAdminRemark(item),
                        "From Location": item?.fromLocation ?? "",
                        "To Location": item?.toLocation ?? "",
                        "Distance (KM)": item?.distanceKm ?? "",
                        "Claimed Amount": item?.claimedAmount ?? "",
                        "Approved Amount": item?.approvedAmount ?? "",
                        "Status": labelize(item?.status),
                        "Bill Number": item?.billNo ?? "",
                        "Bill Date": formatDate(item?.billDate),
                        // "Planned / Visit Time": item?.visit?.plannedTime ?? item?.plannedTime ?? "",
                        // "School Code": item?.visit?.school?.schoolCode ?? item?.school?.schoolCode ?? "",
                        // "School Name": item?.visit?.school?.name ?? item?.school?.name ?? "",
                        // "Party Name": item?.visit?.party?.name ?? item?.party?.name ?? "",
                        // "Payment Reference": item?.paymentRefNo ?? "",
                    });
                });
            });

            const workbook = XLSX.utils.book_new();

            const summarySheet = XLSX.utils.json_to_sheet(detailRows);
            const groupedSummarySheet = XLSX.utils.json_to_sheet(groupedSummaryRows);

            const setColumnWidths = (worksheet, rows) => {
                if (!rows.length) return;
                const keys = Object.keys(rows[0]);
                worksheet["!cols"] = keys.map((key) => {
                    const max = Math.max(
                        key.length,
                        ...rows.slice(0, 100).map((row) => String(row[key] ?? "").split("\n")[0].length)
                    );
                    return { wch: Math.min(Math.max(max + 2, 12), 35) };
                });
                worksheet["!autofilter"] = {
                    ref: XLSX.utils.encode_range({
                        s: { r: 0, c: 0 },
                        e: { r: rows.length, c: Math.max(keys.length - 1, 0) },
                    }),
                };
            };

            setColumnWidths(summarySheet, detailRows);
            setColumnWidths(groupedSummarySheet, groupedSummaryRows);

            XLSX.utils.book_append_sheet(workbook, summarySheet, "Expenses");
            XLSX.utils.book_append_sheet(workbook, groupedSummarySheet, "Grouped Summary");

            XLSX.writeFile(
                workbook,
                `expenses-${new Date().toISOString().slice(0, 10)}.xlsx`
            );

            showSuccess(
                `Excel export completed. ${detailRows.length} individual expenses exported from ${expenses.length} table rows.`
            );
        } catch (error) {
            console.error("[Expenses] Excel export failed:", error);
            showError(error?.message || "Unable to export expenses to Excel.");
        }
    };

    // TABLE COLUMNS

    // ========================================================



    const columns = useMemo(

        () =>

            expenseColumns({

                onEdit: handleEdit,

                onDelete: handleDelete,

                onSubmit: handleSubmit,

                onApprove: handleApprove,

                onReject: handleReject,

                onMarkPaid: handleMarkPaid,

                onView: handleView,

            }),

        [expenses, page, appliedFilters]

    );



    return (

        <div className="mt-5">

            {/* FILTERS */}

            <div

                className="

                    mb-5 rounded-2xl bg-white

                    p-5 shadow-sm dark:bg-navy-800

                "

            >

                <div

                    className="

                        mb-5 flex flex-wrap

                        items-center justify-between gap-3

                    "

                >

                    <h3

                        className="

                            text-lg font-bold

                            text-navy-700 dark:text-white

                        "

                    >

                        Filters

                    </h3>



                    <div className="flex items-center gap-3">

                        <button

                            type="button"

                            onClick={handleExport}

                            className="

                                rounded-lg border

                                border-blue-200 bg-blue-50

                                px-5 py-2.5 text-sm font-semibold

                                text-blue-700 hover:bg-blue-100

                            "

                        >

                            Export Excel

                        </button>



                        <button

                            type="button"

                            onClick={handleClearFilters}

                            className="

                                rounded-lg border

                                border-gray-200 bg-gray-50

                                px-5 py-2.5 text-sm font-semibold

                                text-gray-600 hover:bg-gray-100

                            "

                        >

                            Clear

                        </button>

                    </div>

                </div>



                <div

                    className="

                        grid grid-cols-1 gap-3

                        md:grid-cols-2 lg:grid-cols-6

                    "

                >

                    <select

                        value={filters.employeeId}

                        onChange={(e) =>

                            updateFilter(

                                "employeeId",

                                e.target.value

                            )

                        }

                        className="

                            h-12 rounded-lg border

                            border-gray-200 bg-white

                            px-4 text-sm text-gray-700

                            outline-none focus:border-brand-500

                        "

                    >

                        <option value="">

                            All Employees

                        </option>



                        {employeeOptions.map(

                            (employee) => (

                                <option

                                    key={employee.id}

                                    value={employee.id}

                                >

                                    {employee.name}

                                    {employee.code

                                        ? ` (${employee.code})`

                                        : ""}

                                </option>

                            )

                        )}

                    </select>





                    <select

                        value={filters.status}

                        onChange={(e) =>

                            updateFilter(

                                "status",

                                e.target.value

                            )

                        }

                        className="

                            h-12 rounded-lg border

                            border-gray-200 bg-white

                            px-4 text-sm text-gray-700

                            outline-none focus:border-brand-500

                        "

                    >

                        <option value="">

                            All Status

                        </option>

                        <option value="draft">

                            Draft

                        </option>

                        <option value="submitted">

                            Submitted

                        </option>

                        <option value="approved">

                            Approved

                        </option>

                        <option value="rejected">

                            Rejected

                        </option>

                        <option value="paid">

                            Paid

                        </option>

                        <option value="cancelled">

                            Cancelled

                        </option>

                    </select>



                    <input

                        type="date"

                        value={filters.fromDate}

                        onChange={(e) =>

                            updateFilter(

                                "fromDate",

                                e.target.value

                            )

                        }

                        className="

                            h-12 rounded-lg border

                            border-gray-200 bg-white

                            px-4 text-sm text-gray-700

                            outline-none focus:border-brand-500

                        "

                    />



                    <input

                        type="date"

                        value={filters.toDate}

                        onChange={(e) =>

                            updateFilter(

                                "toDate",

                                e.target.value

                            )

                        }

                        className="

                            h-12 rounded-lg border

                            border-gray-200 bg-white

                            px-4 text-sm text-gray-700

                            outline-none focus:border-brand-500

                        "

                    />

                </div>



                <div className="mt-4 flex justify-end">

                    <button

                        type="button"

                        onClick={handleApplyFilters}

                        className="

                            rounded-lg bg-brand-500

                            px-6 py-2.5 text-sm font-semibold

                            text-white hover:bg-brand-600

                        "

                    >

                        Apply Filters

                    </button>

                </div>

            </div>



            {/* TABLE */}

            <DataTable

                title="Expenses"

                columns={columns}

                data={expenses}

                loading={loading}

                onAdd={handleAdd}

                addButtonText="Add Expense"

            />



            {/* ========================================================

                CREATE / EDIT / VIEW

                ======================================================== */}

            <ExpenseModal

                open={openModal}

                onClose={handleCloseModal}

                reload={loadData}

                item={selectedItem}

                viewMode={modalMode === "view"}

            />

        </div>

    );

}
